import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getAllPositions, selectPosition } from '@store/masterData/positionSlice';
import { ReactText } from 'react';
import { toast } from 'react-toastify';
import { stringResources } from 'src/assets/config/stringResources';
import { RootState } from 'src/redux/store';
import { ContractRateRequest, ContractRateResponse, ContractRatesClient } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { Equal, handleError, showNotFoundErrorNotification } from 'src/utility/utils';
import { RatesDataModel } from 'src/views/contract/vesselContract/Import/DataTypes';

export interface ContractRate extends ContractRateResponse { }

export interface State {
  isLoading: boolean;
  error: boolean;
  contractRateList: ContractRate[];
}

const initialState: State = {
  isLoading: true,
  error: false,
  contractRateList: [],
};

const contractRateSlice = createSlice({
  name: 'contractRate',
  initialState,
  reducers: {
    startLoading: (state) => {
      state.isLoading = true;
    },
    hasError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    getContractRateListSuccess: (state, action: PayloadAction<ContractRate[]>) => {
      state.contractRateList = action.payload;
      state.isLoading = false;
    },
    addNewContractRateSuccess: (state, action: PayloadAction<ContractRate>) => {
      const newItem = action.payload;
      state.contractRateList = [newItem, ...state.contractRateList];
      toast.success(stringResources.REDUX_SLICE.CREATE_SUCCESS);
      state.isLoading = false;
    },
    updateContractRateSuccess: (state, action: PayloadAction<ContractRate>) => {
      const updatedItem = action.payload;
      const index = state.contractRateList.findIndex(item => item.contractRateId === updatedItem.contractRateId);
      state.contractRateList = [...state.contractRateList.slice(0, index), updatedItem, ...state.contractRateList.slice(index + 1)];
      toast.success(stringResources.REDUX_SLICE.UPDATE_SUCCESS);
      state.isLoading = false;
    },
    deleteContractRateSuccess: (state, action: PayloadAction<number>) => {
      const contractRateId = action.payload;
      state.contractRateList = state.contractRateList.filter(item => item.contractRateId !== contractRateId);
      state.isLoading = false;
    },
  },
});

// Reducer
export default contractRateSlice.reducer

// Actions
export const {
  startLoading,
  hasError,
} = contractRateSlice.actions;

// for useSelector
export const selectContractRate = (state: RootState) => state.contractRate as State;

// ----------------------------------------------------------------------

export function getContractRateList(vesselContractId: number) {
  return async (dispatch, getState) => {
    dispatch(contractRateSlice.actions.startLoading());
    try {
      const client = new ContractRatesClient(undefined, axios);
      const response = await client.getByVesselContractId(vesselContractId);
      dispatch(contractRateSlice.actions.getContractRateListSuccess(response));
      return response;
    } catch (error) {
      dispatch(contractRateSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function createContractRate(contractRate: ContractRate) {
  return async (dispatch, getState) => {
    dispatch(contractRateSlice.actions.startLoading());
    try {
      const { contractRateId, ...payload } = contractRate;
      const request = payload as ContractRateRequest;
      const client = new ContractRatesClient(undefined, axios);
      const response = await client.create(request);
      dispatch(contractRateSlice.actions.addNewContractRateSuccess(response));
    } catch (error) {
      dispatch(contractRateSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function createContractRateBatch(vesselContractId: number, ratesData: RatesDataModel[],
  toastId: ReactText, currentProgress: number, totalProgress: number) {
  return async (dispatch, getState) => {
    try {
      await dispatch(getAllPositions());
      const { positionList } = selectPosition(getState());

      const client = new ContractRatesClient(undefined, axios);
      for (const d of ratesData) {
        const positionId = positionList.find(p => Equal(p.positionCode, d.PositionCode))?.positionId;
        if (positionId) {
          const request = {
            vesselContractId,
            positionId,
            dailyRate: d.DailyRate,
            interimDailyRate: d.InterimDailyRate,
            interimPayPercentage: d.InterimPayPercentage,
          } as ContractRateRequest;
          await client.create(request);
          currentProgress += 1;
          toast.update(toastId, { render: `${stringResources.IMPORT.RATES_IMPORTING} (${currentProgress}/${totalProgress})`, progress: currentProgress / totalProgress });
        } else {
          showNotFoundErrorNotification(d.PositionCode);
        }
      }
    } catch (error) {
      handleError(error);
    }
  };
}

export function updateContractRate(contractRate: ContractRate) {
  return async (dispatch, getState) => {
    dispatch(contractRateSlice.actions.startLoading());
    try {
      const { contractRateId, ...payload } = contractRate;
      const request = payload as ContractRateRequest;
      const client = new ContractRatesClient(undefined, axios);
      const response = await client.edit(contractRateId, request);
      dispatch(contractRateSlice.actions.updateContractRateSuccess(response));
    } catch (error) {
      dispatch(contractRateSlice.actions.hasError(true));
      handleError(error);
    }
  };
}

export function deleteContractRate(contractRateId: number, showNotification: boolean = true) {
  return async (dispatch, getState) => {
    dispatch(contractRateSlice.actions.startLoading());
    try {
      const client = new ContractRatesClient(undefined, axios);
      await client.delete(contractRateId);
      dispatch(contractRateSlice.actions.deleteContractRateSuccess(contractRateId));
      if (showNotification) {
        toast.success(stringResources.REDUX_SLICE.DELETE_SUCCESS);
      }
      return true;
    } catch (error) {
      dispatch(contractRateSlice.actions.hasError(true));
      handleError(error);
      return false;
    }
  };
}
